import { createSelector, createSlice } from "@reduxjs/toolkit";
import { generateClient, isCancelError } from 'aws-amplify/api';
import moment from "moment";
import {
    timeEntrybyCrewMemberId,
    timeEntrybyCustomerId,
    timeEntrybyLocationId,
    timeEntrybyScopeId,
    timeEntrybyStatus,
    timeSheetByForemanId,
} from "../graphql/customTimeEntryQueries";
import service from "../service/index";
import { selectUser } from "./auth";
import { popPreviousToken, pushPreviousToken, reset, resetPagination } from "./globalActions";
import { generatePayrollReport, generateTimeEntryReport, sendDailyTimeReport, startLongProcess } from "../graphql/mutations";
import { fetchGroup } from "./group";

const client = generateClient();

const moduleName = "timesheet";

const initialState = {
    loading: false,
    list: [],
    nextToken: null,
    previousTokens: [],
    timeEntries: [],
    teNextToken: null,
    tePreviousTokens: [],
    active: null,
};

const userSlice = createSlice({
    name: moduleName,
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, () => initialState);
        builder.addCase(resetPagination, (state) => {
            state.nextToken = null;
            state.previousTokens = [];
        });
        builder.addCase(popPreviousToken, (state) => {
            state.previousTokens.pop();
        });
        builder.addCase(pushPreviousToken, (state, action) => {
            state.previousTokens.push(action.payload);
        });
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        updateList: (state, action) => {
            state.list = action.payload;
            state.list ??= []
        },
        onCreateTimesheet: (state, action) => {
            const idx = state.list.findIndex((i) => i.id === action.payload.id);
            if (idx < 0) state.list.unshift(action.payload);
            else state.list[idx] = action.payload;
            if (state.active?.id === action.payload.id) {
                state.active = action.payload;
            }
        },
        onUpdateTimesheet: (state, action) => {
            let timesheetIndex = state.list.findIndex((c) => c.id === action.payload.id);
            state.list[timesheetIndex] = action.payload;
            if (state.active?.id === action.payload.id) {
                state.active = action.payload;
            }
        },
        onDeleteTimesheet: (state, action) => {
            let timeSheetIndex = state.list.findIndex((c) => c.id === action.payload.id);
            state.list = [...state.list.slice(0, timeSheetIndex), ...state.list.slice(timeSheetIndex + 1)];
        },
        onGetTimesheet: (state, action) => {
            let timesheetIndex = state.list.findIndex((c) => c.id === action.payload.id);
            if (timesheetIndex >= 0) {
                state.list[timesheetIndex] = action.payload;
            } else {
                state.list.push(action.payload);
            }
            if (state.active?.id === action.payload.id) {
                state.active = action.payload;
            }
        },
        setListTimesheets: (state, action) => {
            state.list = action.payload.items;
            state.nextToken = action.payload.nextToken;
        },
        setActiveTimesheet: (state, action) => {
            state.active = action.payload;
        },
        onUpdateTimeEntry: (state, action) => {
            if (state.active?.id === action.payload.timeSheetId) {
                const teIndex = state.active.timeEntries?.items?.findIndex((te) => te.id === action.payload.id);
                if (teIndex >= 0) {
                    state.active.timeEntries.items[teIndex] = action.payload;
                }
            }

            const teListIndex = state.timeEntries.findIndex((te) => te.id === action.payload.id);
            if (teListIndex >= 0) {
                state.timeEntries[teListIndex] = action.payload;
            } else {
                state.timeEntries.push(action.payload);
            }
        },
        onDeleteTimeEntry: (state, action) => {
            if (state.active?.id === action.payload.timeSheetId) {
                const teIndex = state.active.timeEntries?.items?.findIndex((te) => te.id === action.payload.id);
                if (teIndex >= 0) {
                    state.active.timeEntries.items[teIndex] = action.payload;
                }
            }

            const idx = state.list.findIndex((ts) => ts.id === action.payload.timeSheetId);
            if (idx >= 0) {
                const teIndex = state.list[idx].timeEntries?.items?.findIndex((te) => te.id === action.payload.id);
                if (teIndex >= 0) {
                    state.list[idx].timeEntries.items = [...state.list[idx].timeEntries.items.slice(0, teIndex), ...state.list[idx].timeEntries.items.slice(teIndex + 1)]
                }
            }

            const teListIndex = state.timeEntries.findIndex((te) => te.id === action.payload.id);
            if (teListIndex >= 0) {
                state.timeEntries = [...state.timeEntries.slice(0, teListIndex), ...state.timeEntries.slice(teListIndex + 1)];
            }
        },
        setTimeEntries: (state, action) => {
            state.timeEntries = action.payload.items;
            state.teNextToken = action.payload.nextToken;
        },
        pushTEPreviousToken: (state, action) => {
            state.tePreviousTokens.push(action.payload);
        },
        popTEPreviousToken: (state, action) => {
            state.tePreviousTokens.pop();
        },
        resetTEPagination: (state, action) => {
            state.teNextToken = null;
            state.tePreviousTokens = [];
        },
    },
});

export const {
    setLoading,
    updateList,
    onCreateTimesheet,
    onUpdateTimesheet,
    onDeleteTimesheet,
    onGetTimesheet,
    setListTimesheets,
    setActiveTimesheet,
    onDeleteTimeEntry,
    onUpdateTimeEntry,
    setTimeEntries,
    pushTEPreviousToken,
    popTEPreviousToken,
    resetTEPagination,
} = userSlice.actions;
export default userSlice.reducer;

export const selectState = (state) => state[moduleName];
export const selectLoading = createSelector(selectState, (state) => state.loading);
export const selectTimeSheetByUserId = createSelector(selectState, (state) => state.list);
export const selectTimesheets = createSelector(selectState, (state) => state.list);
export const selectTimeEntries = createSelector(selectState, (state) => state.timeEntries);
export const selectActiveTimesheet = createSelector(selectState, (state) => state.active);
export const selectPagination = createSelector(selectState, (state) => ({ nextToken: state.nextToken, previousTokens: state.previousTokens }));
export const selectTEPagination = createSelector(selectState, (state) => ({ nextToken: state.teNextToken, previousTokens: state.tePreviousTokens }));
export const selectTimesheetById = createSelector([selectTimesheets, (state, id) => id], (timesheets, id) => {
    if (!id) return null;
    return timesheets.find((report) => report.id === id);
});

export const fetchTimeSheetByUserId = () => async (dispatch, getState) => {
    try {
        dispatch(setLoading(true));
        const { username } = selectUser(getState());
        let items = await service.timeSheetByUserId(username);
        dispatch(updateList(items));
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchTimeSheetByForemanId = (foremanId, startDate, endDate) => async (dispatch, getState) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: timeSheetByForemanId,
            variables: {
                foremanId,
                date: {
                    between: [moment(startDate).startOf("day").toISOString(), moment(endDate).endOf("day").toISOString()],
                },
                sortDirection: "DESC",
            },
        });
        dispatch(updateList(data.timeSheetByForemanId.items));
        dispatch(setLoading(false));
    } catch (ex) {
        console.log("Failed to fetch Timesheets for current user", ex);
    }
};

export const createTimeSheet = (timeSheet) => async (dispatch) => {
    const createdTimesheet = await service.createTimesheet(timeSheet);
    if (createdTimesheet) {
        dispatch(onCreateTimesheet(createdTimesheet));
        return createdTimesheet;
    }
};

export const updateTimeSheet = (timeSheet) => async (dispatch) => {
    const updatedTimesheet = await service.updateTimesheet(timeSheet);
    dispatch(onUpdateTimesheet(updatedTimesheet));
};

export const deleteTimeSheet = (timeSheet) => async (dispatch) => {
    const deletedTimesheet = await service.deleteTimesheet(timeSheet);
    dispatch(onDeleteTimesheet(deletedTimesheet));
};

export const getTimeSheet = (id) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const timesheet = await service.getTimesheet(id);
        if (!timesheet) throw new Error("Timesheet no longer exists");
        dispatch(onGetTimesheet(timesheet));
        return timesheet;
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchListTimesheets =
    (locationId, limit = 12, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());
                const listResult = await service.listTimeSheets(locationId, limit, nextToken);
                dispatch(setListTimesheets(listResult));
            } finally {
                dispatch(setLoading(false));
            }
        };

export const modifyTimeEntry = (timeEntry) => async (dispatch) => {
    const newTimeEntry = await service.updateTimeEntry(timeEntry);
    dispatch(onUpdateTimeEntry(newTimeEntry));
    return newTimeEntry;
};

export const createTimeEntry = (timeEntry) => async (dispatch) => {
    const newTimeEntry = await service.createTimeEntry(timeEntry);
    dispatch(onUpdateTimeEntry(newTimeEntry));
    return newTimeEntry;
};

export const deleteTimeEntry = (timeEntry) => async (dispatch) => {
    const deletedTimeEntry = await service.deleteTimeEntry(timeEntry);
    dispatch(onDeleteTimeEntry(deletedTimeEntry));
};

export const fetchTimeEntriesByCustomerId =
    (customerId, locationId, startDate, endDate, filter, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                if (storeNextToken) dispatch(pushTEPreviousToken(nextToken));
                else dispatch(popTEPreviousToken());

                dispatch(setLoading(true));
                const { data } = await client.graphql({
                    query: timeEntrybyCustomerId,
                    variables: {
                        nextToken,
                        customerId,
                        locationId: { eq: locationId },
                        filter: {
                            date: {
                                between: [moment(startDate).startOf("day").toISOString(), moment(endDate).endOf("day").toISOString()],
                            },
                            ...filter,
                        },
                        sortDirection: "DESC",
                    },
                });
                dispatch(setTimeEntries(data.timeEntrybyCustomerId));
            } finally {
                dispatch(setLoading(false));
            }
        };

let fetchTimeEntriesByCrewMemberIdPromise;
let fetchTimeEntriesByCrewMemberIdRequestId = 0;
export const fetchTimeEntriesByCrewMemberId =
    (crewMemberId, locationId, startDate, endDate, filter, nextToken, storeNextToken = true, limit = 999, sortDirection = "DESC", { fetchAll = false, signal } = {}) =>
        async (dispatch) => {
            if (signal?.aborted) return;
            const requestId = ++fetchTimeEntriesByCrewMemberIdRequestId;
            const isCurrentRequest = () => requestId === fetchTimeEntriesByCrewMemberIdRequestId && !signal?.aborted;
            const cancelRequest = () => {
                if (requestId === fetchTimeEntriesByCrewMemberIdRequestId && fetchTimeEntriesByCrewMemberIdPromise) {
                    client.cancel(fetchTimeEntriesByCrewMemberIdPromise, 'cancelled');
                }
            };
            try {
                if (!fetchAll) {
                    if (storeNextToken) dispatch(pushTEPreviousToken(nextToken));
                    else dispatch(popTEPreviousToken());
                }
                dispatch(setLoading(true));
                if (fetchTimeEntriesByCrewMemberIdPromise) client.cancel(fetchTimeEntriesByCrewMemberIdPromise, 'cancelled');
                signal?.addEventListener('abort', cancelRequest);
                const variables = {
                    crewMemberId,
                    locationId: { eq: locationId },
                    filter: {
                        date: {
                            between: [moment(startDate).startOf("day").toISOString(), moment(endDate).endOf("day").toISOString()],
                        },
                        ...filter,
                    },
                    limit,
                    sortDirection,
                };
                const items = [];
                let token = nextToken;
                let result;
                do {
                    const request = client.graphql({ query: timeEntrybyCrewMemberId, variables: { ...variables, nextToken: token } });
                    fetchTimeEntriesByCrewMemberIdPromise = request;
                    const { data } = await request;
                    if (!isCurrentRequest()) return;
                    result = data.timeEntrybyCrewMemberId;
                    if (fetchAll) items.push(...(result.items || []));
                    token = result.nextToken;
                } while (fetchAll && token);
                // Publish a complete calendar range, never an intermediate page.
                const entries = fetchAll ? { items, nextToken: null } : result;
                dispatch(setTimeEntries(entries));
                return entries;
            } catch (ex) {
                if (isCurrentRequest() && !isCancelError(ex)) {
                    throw ex;
                }
            } finally {
                signal?.removeEventListener('abort', cancelRequest);
                if (requestId === fetchTimeEntriesByCrewMemberIdRequestId) {
                    fetchTimeEntriesByCrewMemberIdPromise = undefined;
                    dispatch(setLoading(false));
                }
            }
        };

export const fetchTimeEntriesByScopeId =
    (scopeId, locationId, startDate, endDate, filter, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                if (storeNextToken) dispatch(pushTEPreviousToken(nextToken));
                else dispatch(popTEPreviousToken());

                dispatch(setLoading(true));
                const { data } = await client.graphql({
                    query: timeEntrybyScopeId,
                    variables: {
                        nextToken,
                        scopeId,
                        locationId: { eq: locationId },
                        filter: {
                            date: {
                                between: [moment(startDate).startOf("day").toISOString(), moment(endDate).endOf("day").toISOString()],
                            },
                            ...filter,
                        },
                        sortDirection: "DESC",
                        // limit: 5,
                    },
                });
                dispatch(setTimeEntries(data.timeEntrybyScopeId));
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchTimeEntriesByLocationId =
    (locationId, startDate, endDate, filter, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                if (storeNextToken) dispatch(pushTEPreviousToken(nextToken));
                else dispatch(popTEPreviousToken());

                dispatch(setLoading(true));
                const { data } = await client.graphql({
                    query: timeEntrybyLocationId,
                    variables: {
                        nextToken,
                        locationId,
                        filter,
                        date: {
                            between: [moment(startDate).startOf("day").toISOString(), moment(endDate).endOf("day").toISOString()],
                        },
                        sortDirection: "DESC",
                        limit: 999,
                    },
                });
                dispatch(setTimeEntries(data.timeEntrybyLocationId));
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchTimeEntriesByStatus =
    (locationId, status = "ERROR", limit, filter, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                if (storeNextToken) dispatch(pushTEPreviousToken(nextToken));
                else dispatch(popTEPreviousToken());

                dispatch(setLoading(true));
                const { data } = await client.graphql({
                    query: timeEntrybyStatus,
                    variables: {
                        nextToken,
                        status,
                        locationId: { eq: locationId },
                        filter,
                        limit,
                    },
                });
                dispatch(setTimeEntries(data.timeEntrybyStatus));
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchTimeEntriesByGroups =
    (locationId, groups, startDate, endDate, filter, nextToken, storeNextToken = true) =>
        async (dispatch) => {
            try {
                if (storeNextToken) dispatch(pushTEPreviousToken(nextToken));
                else dispatch(popTEPreviousToken());

                console.log('here', filter);

                dispatch(setLoading(true));
                let hydratedGroups = [];
                for await (const groupId of groups) {
                    const group = await dispatch(fetchGroup(groupId, locationId));
                    hydratedGroups.push(group);
                }

                const crewMemberFilter = hydratedGroups.reduce((rtn, group) => {
                    return { or: [...rtn.or, ...group.members.slice(0, 50).map(member => ({ crewMemberId: { eq: member.id } }))] }
                }, { or: [] })

                const { data } = await client.graphql({
                    query: timeEntrybyLocationId,
                    variables: {
                        nextToken,
                        locationId,
                        date: {
                            between: [moment(startDate).startOf("day").toISOString(), moment(endDate).endOf("day").toISOString()],
                        },
                        sortDirection: "DESC",
                        filter: {
                            ...filter,
                            ...crewMemberFilter,
                        }
                    },
                });
                dispatch(setTimeEntries(data.timeEntrybyLocationId));
            } finally {
                dispatch(setLoading(false));
            }
        };

const runLongProcess = (processName, params) => async (dispatch) => {
    const { data } = await client.graphql({
        query: startLongProcess,
        variables: {
            input: {
                processName,
                params: JSON.stringify(params)
            },
        },
    });
    return data.startLongProcess;
}

export const fetchTimeEntryReport = (params) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { status, message } = await runLongProcess("timeEntryReport", params)(dispatch)
        if (!status) throw new Error(message);
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchPayrollReport = (params) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const reportParams = {
            ...params,
            PDF: true,
            filters: [],
            reportType: 'byLocation',
            startDate: moment(params.startDate).startOf('d').toISOString(),
            endDate: moment(params.startDate).add(13, 'd').endOf('day').toISOString(),
        }
        const { status, message } = await runLongProcess("timeEntryReport", reportParams)(dispatch)
        if (!status) throw new Error(message);
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchDailyTimeReports = () => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { status, message } = await runLongProcess("dailyTimeReport")(dispatch)
        if (!status) throw new Error(message);
    } finally {
        dispatch(setLoading(false));
    }
};


export const fetchUserListReport = () => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { status, message } = await runLongProcess("userList")(dispatch)
        if (!status) throw new Error(message);
    } finally {
        dispatch(setLoading(false));
    }
};